import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useCardController, useDialog } from "@datanac/datanac-mui-toolkit";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Box, CircularProgress, FormControl, Grid, InputLabel, Select, Stack, useMediaQuery } from "@mui/material";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { AppContext } from "App";
import BudgetCountyCard from "components/Budget/components/BudgetCountyCard";
import {
  BUDGET_CATEGORY_DIRECT,
  BUDGET_CATEGORY_INDIRECT,
  BUDGET_ITEMS_DIRECT,
  BUDGET_ITEMS_INDIRECT,
  BUDGET_STATUS_ACTIVE,
  BUDGET_STATUS_DRAFT,
  getBudgetYears,
} from "components/Budget/helpers";
import { deriveProjectedPrice } from "components/Budget/projectedPrice";
import EmptyCard from "components/Cards/EmptyCard";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { BudgetIcon } from "components/Icons/Icons";
import { useBlockerPrompt } from "components/Menu/hooks/useBlockerPrompt";
import { useFarmController } from "components/Vault/Farm/useFarmController";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import _ from "lodash";
import { SkeletonWrapper } from "mui-toolkit/SkeletonWrapper";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { formatNumber, formatPrice } from "utility/formatters";
import { v4 as uuidv4 } from "uuid";

import { UsersApiHelper } from "../../api/ApiHelper";
import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";

export default function BudgetWorkspace() {
  const { globalState } = useContext(AppContext);
  const { addAttachments } = useChatAssistantAttachments();
  const [state, dispatch] = useReducer(propUpdateReducer, {
    status: BUDGET_STATUS_DRAFT,
    is_active: true,
  });
  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const { budget_id } = useParams();
  const navigate = useNavigate();

  const isXL = useMediaQuery((theme) => theme.breakpoints.up("xl"), { noSsr: true });
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingBudgetDetails, setIsLoadingBudgetDetails] = useState(true);
  const [isLoadingCrops, setIsLoadingCrops] = useState(true);
  const { component: formStatusComponent, actions: formStatusActions, state: formStatusState } = useFormStatus();

  const { state: countyCards, actions: countyCardActions } = useCardController();
  const { component: confirmDialogComponent } = useDialog();
  const isValid = useCallback(() => {
    return Boolean(state?.id && state?.name && state?.year);
  }, [state]);
  useBlockerPrompt(!isValid(), "BudgetWorkspace");
  const { countyPractices } = useFarmController({
    crop_year: state.year,
    onError: handleError,
  });
  const loadBudgetData = async (_budget_id) => {
    UsersApiHelper.selectBudgets({ id: _budget_id, is_active: true }, globalState)
      .then((currentBudget) => {
        if (currentBudget?.length) {
          dispatch({
            type: "updateMany",
            payload: currentBudget[0],
          });
        }
      })
      .catch((err) => {
        handleError(err);
      })
      .finally(() => {
        setIsLoadingBudgetDetails(false);
      });

    UsersApiHelper.selectBudgetCountyPractices(
      {
        budget_id: _budget_id,
        is_active: true,
      },
      globalState
    )
      .then((currentBudgetCountyPractices) => {
        countyCardActions.replace(currentBudgetCountyPractices.filter((cp) => cp.is_active));
      })
      .catch((err) => {
        handleError(err);
      })
      .finally(() => {
        setIsLoadingCrops(false);
      });
  };

  useEffect(() => {
    if (String(budget_id) === "0") {
      dispatch({
        type: "update",
        payload: { key: "id", value: uuidv4() },
      });
      setIsLoadingBudgetDetails(false);
      setIsLoadingCrops(false);
    } else {
      loadBudgetData(budget_id);
      dispatch({
        type: "update",
        payload: { key: "id", value: budget_id },
      });
    }
  }, [globalState?.producer_token, budget_id]);

  const handleChangeStateSelect = async (event) => {
    const { name, value } = event.target;
    if (state[name] === value) return;

    formStatusActions.setIsFormDirty(true, false);

    if (name !== "year") {
      dispatch({
        type: "update",
        payload: { key: name, value: value },
      });
      return;
    }

    const previousYear = state?.year;
    const currentBudgetId = state?.id;
    let nextStatus = state?.status || BUDGET_STATUS_DRAFT;

    try {
      if (globalState?.producer_token && previousYear && currentBudgetId) {
        const previousYearBudgets = await UsersApiHelper.selectBudgets(
          {
            producer_token: globalState.producer_token,
            year: previousYear,
            is_active: true,
          },
          globalState
        );
        const remainingBudgets = (previousYearBudgets || []).filter((budget) => budget.id !== currentBudgetId);
        if (remainingBudgets.length === 1 && remainingBudgets[0].status === BUDGET_STATUS_DRAFT) {
          await UsersApiHelper.updateBudget(
            {
              ...remainingBudgets[0],
              producer_token: globalState.producer_token,
              status: BUDGET_STATUS_ACTIVE,
              checksum: null,
            },
            globalState
          );
        }
      }

      if (globalState?.producer_token && value && currentBudgetId) {
        const nextYearBudgets = await UsersApiHelper.selectBudgets(
          {
            producer_token: globalState.producer_token,
            year: value,
            is_active: true,
          },
          globalState
        );
        const otherBudgets = (nextYearBudgets || []).filter((budget) => budget.id !== currentBudgetId);
        nextStatus = otherBudgets.length === 0 ? BUDGET_STATUS_ACTIVE : BUDGET_STATUS_DRAFT;
      }
    } catch (err) {
      handleError(err);
    }

    dispatch({
      type: "updateMany",
      payload: {
        year: value,
        status: nextStatus,
      },
    });
  };

  const handleChangeState = (event, value) => {
    value = event.target.value;

    if (state[event.target.name] !== value) {
      formStatusActions.setIsFormDirty(true, false);

      dispatch({
        type: "update",
        payload: { key: event.target.name, value: value },
      });
    }
  };

  useEffect(() => {
    if (isValid() && globalState?.producer_token && formStatusState?.isFormDirty) {
      formStatusActions.setIsFormDirty(true, true);

      UsersApiHelper.updateBudget({ ...state, checksum: null }, globalState)
        .then(() => {
          formStatusActions.setIsSaving(false);
        })
        .catch((err) => {
          console.warn(err);
          formStatusActions.setIsFormDirty(true, false);
          enqueueSnackbar(err.detail || err.message || JSON.stringify(err), {
            preventDuplicate: true,
            variant: "error",
          });
        });
    }
  }, [state]);

  const createLineItem = (category, name, value, budget_id) => ({
    id: uuidv4(),
    producer_token: globalState.producer_token,
    agent_token: globalState.agent_token,
    budget_county_practice_id: budget_id,
    category,
    name,
    value,
    is_active: true,
  });
  /**
   * Creates an array of default line items for a given budget.
   *
   * This function initializes budget line items for both direct and indirect categories.
   * It extracts the name (second element) from each item in `BUDGET_ITEMS_DIRECT` and
   * `BUDGET_ITEMS_INDIRECT`, assigns a default amount of 0.00, and associates it with
   * the given budget ID.
   */
  const createDefaultLineItems = (budget_id) => [
    ...BUDGET_ITEMS_DIRECT.map((lineItem) => createLineItem(BUDGET_CATEGORY_DIRECT, lineItem[1], 0.0, budget_id)),
    ...BUDGET_ITEMS_INDIRECT.map((lineItem) => createLineItem(BUDGET_CATEGORY_INDIRECT, lineItem[1], 0.0, budget_id)),
  ];

  const isCountyPracticesApiCalled = useRef(false);

  const loadCountyPracticesFromFarmVault = async () => {
    if (countyCards.length > 0) return;

    try {
      formStatusActions.setIsSaving(true);

      const [positionSummary, cropProfiles] = await Promise.all([
        UsersApiHelper.users_search(
          "PositionSummary",
          { year: state?.year, producer_token: globalState?.producer_token },
          globalState
        ),
        UsersApiHelper.users_search(
          "CropProfile",
          { producer_token: globalState?.producer_token, year: state?.year, is_active: true },
          globalState
        ),
      ]);
      const boardPriceByCommodity = new Map(
        (positionSummary ?? []).map((row) => [row.commodity_name, row.market_last_price])
      );
      const basisByCommodity = new Map((cropProfiles ?? []).map((row) => [row.commodity_name, row.basis]));

      const transformedData = countyPractices.map((item) => ({
        id: uuidv4(),
        budget_id: state?.id,
        budget_name: state?.name,
        year: state?.year,
        state_name: item.location_state_name,
        county_name: item.location_county_name,
        commodity_name: item.commodity_name,
        type_name: item.type_name,
        practice_name: item.practice_name,
        reported_acreage: item.reported_acreage || 0,
        yield: item.approved_yield_weighted || 0,
        projected_price: deriveProjectedPrice(
          boardPriceByCommodity.get(item.commodity_name),
          basisByCommodity.get(item.commodity_name)
        ),
        is_active: true,
      }));

      const requestBody = {
        BudgetCountyPractice: transformedData,
        budget_id: state?.id,
        producer_token: globalState.producer_token,
      };

      const newLineItems = transformedData.reduce((acc, data) => {
        return [...acc, ...createDefaultLineItems(data.id)];
      }, []);

      const response = await UsersApiHelper.updateBudgetCountyPractice(requestBody, globalState);
      if (!response || response.rowaffected === 0) {
        throw new Error("Failed to update budget county practice");
      }

      await UsersApiHelper.updateBudgetCountyPracticeLineItem(
        { BudgetCountyPracticeLineItems: newLineItems },
        globalState
      );
    } catch (error) {
      console.warn(error);
      formStatusActions.setIsFormDirty(true, false);
      enqueueSnackbar(error.detail || error.message || JSON.stringify(error), {
        preventDuplicate: true,
        variant: "error",
      });
    } finally {
      formStatusActions.setIsSaving(false);
      loadBudgetData(state?.id);
      handleBudgetSave();
    }
  };

  useEffect(() => {
    if (
      !isCountyPracticesApiCalled.current &&
      isValid() &&
      Boolean(countyPractices?.length) &&
      state?.id &&
      !countyCards?.length
    ) {
      isCountyPracticesApiCalled.current = true;
      loadCountyPracticesFromFarmVault();
    }
  }, [countyPractices, countyCards, budget_id, state]);

  const groupAndSortCountyCards = (cards) => {
    return Object.values(
      cards.reduce((acc, card) => {
        const key = `${card.county_name}, ${card.state_name}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(card);
        return acc;
      }, {})
    ).map((group) =>
      group.sort(
        (a, b) =>
          a.commodity_name.localeCompare(b.commodity_name) ||
          a.type_name.localeCompare(b.type_name) ||
          (a.name || "").localeCompare(b.name || "")
      )
    );
  };

  // Memoize the grouped and sorted county cards to prevent unnecessary recalculations
  const groupedAndSortedCountyCards = useMemo(() => {
    return groupAndSortCountyCards(countyCards);
  }, [countyCards]);

  // Add chat attachments with budget cards data - optimize to prevent infinite loops
  useEffect(() => {
    if (countyCards?.length && state?.name) {
      addAttachments([
        {
          name: `Budget: ${state.name} (${state.year || "No Year"})`,
          content: countyCards,
        },
      ]);
    }
  }, [countyCards, state?.name, state?.year]); // Remove addAttachments from dependencies

  // Add detailed budget attachments - now properly memoized
  useEffect(() => {
    if (groupedAndSortedCountyCards?.length) {
      addAttachments([{ name: "Budget Details", content: groupedAndSortedCountyCards }]);
    }
  }, [groupedAndSortedCountyCards]); // Remove addAttachments from dependencies

  const handleCardOnChange = async (_newState) => {
    const { budget_id, ...filteredState } = _newState;
    const requestBody = {
      BudgetCountyPractice: [filteredState],
      budget_id,
      producer_token: globalState.producer_token,
    };
    await UsersApiHelper.updateBudgetCountyPractice(requestBody, globalState);
    loadBudgetData(budget_id);
  };

  const handleAddNewClick = () => {
    navigate(`/budget/${state?.id}/0`);
  };

  const handleBudgetSave = () => {
    navigate(`/budget/${state?.id}`, { replace: true });
  };

  return (
    <>
      <WorkspaceHeader
        title={state?.name || ""}
        category={{
          title: "Budgets",
          icon: <BudgetIcon />,
        }}
        breadCrumbs={[
          {
            title: "Farm Budgets",
            path: "/budget",
          },
        ]}
      />

      <WorkspacePageHeader title="Budget Details" additionalControls={formStatusComponent} />
      <Grid container spacing={1}>
        <Grid item xs={6} md={3}>
          <FormControl fullWidth={true}>
            <StatefulControlledTextField
              label="Budget Name"
              name="name"
              isRequired={true}
              isLoading={isLoadingBudgetDetails}
              defaultValue={state.name || ""}
              variant="outlined"
              onChange={handleChangeState}
              InputLabelProps={{ shrink: true }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControl fullWidth>
            <SkeletonWrapper variant="textfield" sx={{ marginBottom: "20px" }} isLoading={isLoadingBudgetDetails}>
              <InputLabel id="selectYearLabel">Year</InputLabel>
              <Select
                labelId="selectYearLabel"
                value={state?.year || ""}
                name={"year"}
                onChange={handleChangeStateSelect}
                label={"Year"}
              >
                {getBudgetYears()?.map((currentItem, index) => {
                  return (
                    <MenuItem key={index} value={currentItem}>
                      {currentItem}
                    </MenuItem>
                  );
                })}
              </Select>
            </SkeletonWrapper>
          </FormControl>
        </Grid>
      </Grid>

      <Divider />

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h1">Crops</Typography>
        {isLoadingCrops && <CircularProgress data-testid="loading-county-practices" />}
      </Stack>
      {groupedAndSortedCountyCards.map((countyGroup, countyIndex) => (
        <div key={countyIndex}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {countyGroup[0].county_name}, {countyGroup[0].state_name}
          </Typography>
          <Box className="cardContainer">
            {countyGroup.map((currentBudgetCountyPractice, index) => (
              <BudgetCountyCard
                key={`${countyIndex}/${index}`}
                budgetCountyPractice={currentBudgetCountyPractice}
                onChange={handleCardOnChange}
              />
            ))}
          </Box>
          <br />
        </div>
      ))}
      <Box className="cardContainer">
        {!countyCards?.length && (
          <EmptyCard text="Start by adding a new crop to this budget" isLoading={isLoadingCrops} />
        )}
      </Box>

      <Box
        className="pageBreakBefore"
        sx={{
          ...(isXL && {
            top: "auto",
            bottom: 0,
            pb: 2,
            position: "sticky",
            width: "100%",
            backgroundColor: (theme) => theme.palette.background.paper,
            zIndex: 1000,
          }),
        }}
      >
        <Divider sx={{ mt: 2, mb: 2 }} />
        <Grid container justifyItems="flex-start" spacing={1}>
          <Grid item xs={12} md={6} xl={1}>
            <Typography variant="h1">Totals</Typography>
          </Grid>
          <Grid item xs={12} md={6} xl={1.5}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">{formatNumber(_.sumBy(countyCards, "reported_acreage"), 2)}</Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Acres
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.5} sx={{ backgroundColor: "grey.100" }}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">
                {formatPrice(
                  _.sum(countyCards.map((c) => c.revenue_simple)) / _.sum(countyCards.map((c) => c.reported_acreage)),
                  0
                )}
              </Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Rev/Acre
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.7} sx={{ backgroundColor: "grey.100" }}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">{formatPrice(_.sum(countyCards.map((c) => c.revenue_simple)), 0)}</Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Revenue
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.5}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">
                {formatPrice(
                  _.sum(countyCards.map((c) => c.production_cost)) / _.sum(countyCards.map((c) => c.reported_acreage)),
                  0
                )}
              </Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Cost/Acre
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.7}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">{formatPrice(_.sum(countyCards.map((c) => c.production_cost)), 0)}</Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Cost
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.5} sx={{ backgroundColor: "grey.100" }}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5">
                {formatPrice(
                  _.sum(countyCards.map((c) => c.revenue_simple - c.production_cost)) /
                    _.sum(countyCards.map((c) => c.reported_acreage)),
                  0
                )}
              </Typography>
              <Typography variant="body1" sx={{ textTransform: isSmall ? "" : "uppercase" }}>
                Net Income/Acre
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} xl={1.5} sx={{ backgroundColor: "grey.100" }}>
            <Stack direction={isSmall ? "row-reverse" : "column"} justifyContent="space-between">
              <Typography variant="h5" sx={{ fontWeight: "bold !important" }}>
                {formatPrice(_.sum(countyCards.map((c) => c.revenue_simple - c.production_cost)), 0)}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold !important", textTransform: isSmall ? "" : "uppercase" }}
              >
                Net Income
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <WorkspaceActionBar
        content={
          <Button variant="contained" disabled={!isValid()} onClick={handleAddNewClick}>
            <AddCircleOutline sx={{ mr: 1 }} />
            New Crop
          </Button>
        }
      />

      {confirmDialogComponent}
    </>
  );
}
